import React, {useState} from 'react';
import AnimateHeight from 'react-animate-height';
import DOMPurify from 'dompurify';
import classNames from 'classnames';

import {Container} from './../container';
import {Icon} from './../icon/icon';
import {Grid, GridColumn} from '../grid';

export interface INotificationProps {
    text: string | JSX.Element;
    type: 'error' | 'info' | 'success' | 'announcement';
    className?: string;
    defaultIsOpen?: boolean;
    title?: string;
    isClosable?: boolean;
    hasClickableUrl?: boolean;
    onClose?: () => void;
}

const Notification = ({
    type = 'error',
    className,
    defaultIsOpen = false,
    title,
    text,
    hasClickableUrl,
    isClosable,
    onClose,
}: INotificationProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen);
    const computedClassName: string = classNames(
        'notification',
        {
            [`notification--type-${type}`]: type,
            'is-open': isOpen,
        },
        className,
    );

    const makeLinksClickable = (t: string): string => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const linkedText = t.replace(
            urlRegex,
            (url) =>
                `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
        );

        return DOMPurify.sanitize(linkedText, { ALLOWED_TAGS: ['a'], ALLOWED_ATTR: ['href', 'target', 'rel'] });
    };

    return (
        <Container className={computedClassName}>
            <AnimateHeight duration={200} height={isOpen ? 'auto' : 0} easing="ease-in-out">
                <div className="notification__inner">
                    <Grid align={['middle-xs']} gutter="small">
                        <GridColumn align={['top-xs']} width={['min-xs']}>
                            <Icon className="notification__icon" name="warning" />
                        </GridColumn>
                        <GridColumn width={['max-xs']}>
                            <div className="text">
                                {title && <h4>{title}</h4>}
                                {typeof text === 'string' ? (
                                    hasClickableUrl ? (
                                        <p
                                            style={{ whiteSpace: 'pre-line' }}
                                            dangerouslySetInnerHTML={{ __html: makeLinksClickable(text) }}
                                        />
                                    ) : (
                                        <p style={{ whiteSpace: 'pre-line' }}>{text}</p>
                                    )
                                ) : (
                                    text
                                )}

                            </div>
                        </GridColumn>
                        {isClosable && (
                            <button className="notification__close" onClick={() => {
                                setIsOpen(false);
                                if (onClose) {
                                    onClose();
                                }
                            }}>
                                <Icon name="close" />
                            </button>
                        )}
                    </Grid>
                </div>
            </AnimateHeight>
        </Container>
    );
};

export default Notification;
